import {
  RoiSelectorColumn,
  RoiSelectorSection,
} from "./roi-request-create.types";

const createRows = <T extends Record<string, string>>(
  prefix: string,
  rows: T[],
) =>
  rows.map((row, index) => ({
    data_element_id: `${prefix}-${index + 1}`,
    ...row,
  }));

const summaryColumns: RoiSelectorColumn[] = [
  { key: "name", header: "Name", sortable: true },
  { key: "category", header: "Category", sortable: true },
  { key: "status", header: "Status", sortable: true },
  { key: "recorded_date", header: "Recorded Date", sortable: true },
];

export const ROI_REQUEST_SELECTOR_SECTIONS: RoiSelectorSection[] = [
  {
    section_id: "allergies",
    label: "Allergies",
    date_filter_keys: ["recorded_date"],
    table: {
      columns: [
        { key: "allergen", header: "Allergen", sortable: true },
        { key: "reaction", header: "Reaction", sortable: true },
        { key: "severity", header: "Severity", sortable: true },
        { key: "recorded_date", header: "Recorded Date", sortable: true },
      ],
      rows: createRows("allergy", [
        {
          allergen: "Penicillin",
          reaction: "Rash",
          severity: "Moderate",
          recorded_date: "2025-11-16",
        },
        {
          allergen: "Peanuts",
          reaction: "Anaphylaxis",
          severity: "Severe",
          recorded_date: "2025-10-02",
        },
        {
          allergen: "Latex",
          reaction: "Swelling",
          severity: "Mild",
          recorded_date: "2025-08-24",
        },
        {
          allergen: "Shellfish",
          reaction: "Hives",
          severity: "Moderate",
          recorded_date: "2025-07-18",
        },
        {
          allergen: "Sulfa Drugs",
          reaction: "Nausea",
          severity: "Mild",
          recorded_date: "2025-06-14",
        },
        {
          allergen: "Bee Stings",
          reaction: "Shortness of Breath",
          severity: "Severe",
          recorded_date: "2025-05-06",
        },
        {
          allergen: "Ibuprofen",
          reaction: "Facial Swelling",
          severity: "Moderate",
          recorded_date: "2025-04-22",
        },
        {
          allergen: "Eggs",
          reaction: "GI Upset",
          severity: "Mild",
          recorded_date: "2025-03-09",
        },
        {
          allergen: "Aspirin",
          reaction: "Wheezing",
          severity: "Moderate",
          recorded_date: "2025-02-11",
        },
        {
          allergen: "Contrast Dye",
          reaction: "Flushing",
          severity: "Moderate",
          recorded_date: "2025-01-28",
        },
      ]),
    },
  },
  {
    section_id: "directives",
    label: "Directives",
    date_filter_keys: ["effective_date"],
    table: {
      columns: [
        { key: "directive_type", header: "Directive Type", sortable: true },
        { key: "status", header: "Status", sortable: true },
        { key: "effective_date", header: "Effective Date", sortable: true },
        { key: "source", header: "Source", sortable: true },
      ],
      rows: createRows("directive", [
        {
          directive_type: "Advance Directive",
          status: "Active",
          effective_date: "2025-09-14",
          source: "Scanned Intake Packet",
        },
        {
          directive_type: "DNR",
          status: "Active",
          effective_date: "2025-06-09",
          source: "Provider Order",
        },
        {
          directive_type: "POLST",
          status: "Archived",
          effective_date: "2024-12-11",
          source: "External Facility",
        },
        {
          directive_type: "HIPAA Restriction",
          status: "Active",
          effective_date: "2025-02-03",
          source: "Patient Portal",
        },
        {
          directive_type: "Consent to Release",
          status: "Expired",
          effective_date: "2024-10-21",
          source: "Front Desk Intake",
        },
        {
          directive_type: "Medical Power of Attorney",
          status: "Active",
          effective_date: "2024-08-30",
          source: "Legal Upload",
        },
        {
          directive_type: "Organ Donor Preference",
          status: "Active",
          effective_date: "2024-07-05",
          source: "State Registry Sync",
        },
        {
          directive_type: "Blood Product Refusal",
          status: "Archived",
          effective_date: "2024-05-18",
          source: "Legacy Chart",
        },
      ]),
    },
  },
  {
    section_id: "flowsheets",
    label: "Flowsheets",
    date_filter_keys: ["obs_date"],
    table: {
      columns: [
        { key: "obs_date", header: "Obs Date", sortable: true },
        { key: "group_name", header: "Group Name", sortable: true },
        { key: "obs_name", header: "Obs Name", sortable: true },
        { key: "obs_value", header: "Obs Value", sortable: true },
      ],
      rows: createRows("flowsheet", [
        {
          obs_date: "08/21/2007",
          group_name: "Skin",
          obs_name: "SKIN SQ INSP",
          obs_value: "intact without lesions or rashes.",
        },
        {
          obs_date: "08/21/2007",
          group_name: "SH",
          obs_name: "SOCIAL HX",
          obs_value:
            "Patient has never smoked. Passive smoke exposure - no Alcohol Use - no Drug Use - no HIV/High Risk - no Regular Exercise.",
        },
        {
          obs_date: "08/21/2007",
          group_name: "SH",
          obs_name: "SH REVIEWED",
          obs_value: "reviewed - no changes required",
        },
        {
          obs_date: "08/21/2007",
          group_name: "ROS",
          obs_name: "ROS SKIN",
          obs_value:
            "Denies acne, dry skin, excessive perspiration, hair loss, nail problems, rash, suspicious lesions.",
        },
        {
          obs_date: "08/21/2007",
          group_name: "ROS",
          obs_name: "ROS:PULMON",
          obs_value:
            "Denies chest congestion, cough, cyanosis, excessive sputum, shortness of breath, wheezing.",
        },
        {
          obs_date: "09/03/2007",
          group_name: "Vitals",
          obs_name: "BP",
          obs_value: "122/74",
        },
        {
          obs_date: "09/03/2007",
          group_name: "Vitals",
          obs_name: "Temp",
          obs_value: "98.4 F",
        },
        {
          obs_date: "09/03/2007",
          group_name: "Pain",
          obs_name: "PAIN SCORE",
          obs_value: "2/10",
        },
        {
          obs_date: "09/17/2007",
          group_name: "Resp",
          obs_name: "O2 SAT",
          obs_value: "98%",
        },
        {
          obs_date: "09/17/2007",
          group_name: "Resp",
          obs_name: "RESP RATE",
          obs_value: "16",
        },
        {
          obs_date: "10/01/2007",
          group_name: "Cardiac",
          obs_name: "HEART RATE",
          obs_value: "72",
        },
        {
          obs_date: "10/01/2007",
          group_name: "Neuro",
          obs_name: "ORIENTATION",
          obs_value: "Alert and oriented x4",
        },
        {
          obs_date: "10/15/2007",
          group_name: "GI",
          obs_name: "APPETITE",
          obs_value: "Good",
        },
        {
          obs_date: "10/15/2007",
          group_name: "Mobility",
          obs_name: "GAIT",
          obs_value: "Steady without assistance",
        },
      ]),
    },
  },
  {
    section_id: "history",
    label: "History",
    table: {
      columns: [],
      rows: [],
    },
  },
  {
    section_id: "immunizations",
    label: "Immunizations",
    date_filter_keys: ["recorded_date"],
    table: {
      columns: summaryColumns,
      rows: createRows("immunization", [
        {
          name: "Influenza",
          category: "Vaccine",
          status: "Completed",
          recorded_date: "2025-10-12",
        },
        {
          name: "COVID-19 Booster",
          category: "Vaccine",
          status: "Completed",
          recorded_date: "2025-09-01",
        },
        {
          name: "Tdap",
          category: "Vaccine",
          status: "Completed",
          recorded_date: "2024-11-19",
        },
        {
          name: "Shingrix Dose 1",
          category: "Vaccine",
          status: "Completed",
          recorded_date: "2024-07-07",
        },
        {
          name: "Pneumococcal PCV20",
          category: "Vaccine",
          status: "Completed",
          recorded_date: "2024-03-28",
        },
      ]),
    },
  },
  {
    section_id: "medications",
    label: "Medications",
    date_filter_keys: ["recorded_date"],
    table: {
      columns: summaryColumns,
      rows: createRows("medication", [
        {
          name: "Lisinopril 10 mg",
          category: "Antihypertensive",
          status: "Active",
          recorded_date: "2026-02-18",
        },
        {
          name: "Metformin 500 mg",
          category: "Diabetes",
          status: "Active",
          recorded_date: "2026-02-11",
        },
        {
          name: "Atorvastatin 20 mg",
          category: "Lipid Lowering",
          status: "Active",
          recorded_date: "2026-01-24",
        },
        {
          name: "Albuterol HFA",
          category: "Respiratory",
          status: "PRN",
          recorded_date: "2025-12-05",
        },
        {
          name: "Omeprazole 20 mg",
          category: "GI",
          status: "Active",
          recorded_date: "2025-11-02",
        },
        {
          name: "Prednisone 10 mg",
          category: "Steroid",
          status: "Completed",
          recorded_date: "2025-09-14",
        },
      ]),
    },
  },
  {
    section_id: "orders",
    label: "Orders",
    date_filter_keys: ["recorded_date"],
    table: {
      columns: summaryColumns,
      rows: createRows("order", [
        {
          name: "CBC with Differential",
          category: "Lab",
          status: "Ordered",
          recorded_date: "2026-03-19",
        },
        {
          name: "Chest X-Ray",
          category: "Imaging",
          status: "Completed",
          recorded_date: "2026-03-12",
        },
        {
          name: "Urinalysis",
          category: "Lab",
          status: "In Progress",
          recorded_date: "2026-03-10",
        },
        {
          name: "Referral to Cardiology",
          category: "Referral",
          status: "Ordered",
          recorded_date: "2026-02-27",
        },
        {
          name: "BMP",
          category: "Lab",
          status: "Completed",
          recorded_date: "2026-02-20",
        },
      ]),
    },
  },
  {
    section_id: "problems",
    label: "Problems",
    date_filter_keys: ["recorded_date"],
    table: {
      columns: summaryColumns,
      rows: createRows("problem", [
        {
          name: "Type 2 Diabetes Mellitus",
          category: "Chronic",
          status: "Active",
          recorded_date: "2025-12-18",
        },
        {
          name: "Essential Hypertension",
          category: "Chronic",
          status: "Active",
          recorded_date: "2025-11-03",
        },
        {
          name: "Hyperlipidemia",
          category: "Chronic",
          status: "Active",
          recorded_date: "2025-10-07",
        },
        {
          name: "Acute Bronchitis",
          category: "Acute",
          status: "Resolved",
          recorded_date: "2025-08-29",
        },
        {
          name: "Left Shoulder Pain",
          category: "Musculoskeletal",
          status: "Active",
          recorded_date: "2025-07-13",
        },
      ]),
    },
  },
  {
    section_id: "documents",
    label: "Documents",
    date_filter_keys: ["created_at"],
    table: {
      columns: [
        { key: "document_name", header: "Document Name", sortable: true },
        { key: "document_type", header: "Type", sortable: true },
        { key: "created_at", header: "Created", sortable: true },
        { key: "uploaded_by", header: "Uploaded By", sortable: true },
      ],
      rows: createRows("document", [
        {
          document_name: "Discharge Summary",
          document_type: "Summary",
          created_at: "2026-02-20",
          uploaded_by: "Michael Johnson",
        },
        {
          document_name: "Operative Report",
          document_type: "Procedure",
          created_at: "2026-01-09",
          uploaded_by: "John Smith",
        },
        {
          document_name: "Face Sheet",
          document_type: "Administrative",
          created_at: "2025-12-22",
          uploaded_by: "Emily Davis",
        },
        {
          document_name: "Consult Note",
          document_type: "Clinical",
          created_at: "2025-12-14",
          uploaded_by: "Sarah Parker",
        },
        {
          document_name: "Medication Reconciliation",
          document_type: "Medication",
          created_at: "2025-11-30",
          uploaded_by: "Linda Green",
        },
        {
          document_name: "Insurance Card",
          document_type: "Administrative",
          created_at: "2025-11-04",
          uploaded_by: "Jessica Brown",
        },
        {
          document_name: "Referral Authorization",
          document_type: "Authorization",
          created_at: "2025-10-17",
          uploaded_by: "Daniel Cooper",
        },
        {
          document_name: "Pathology Report",
          document_type: "Result",
          created_at: "2025-09-08",
          uploaded_by: "Michael Johnson",
        },
        {
          document_name: "Post-Op Instructions",
          document_type: "Patient Education",
          created_at: "2025-08-12",
          uploaded_by: "Emily Davis",
        },
        {
          document_name: "Progress Note",
          document_type: "Clinical",
          created_at: "2025-07-25",
          uploaded_by: "John Smith",
        },
      ]),
    },
  },
  {
    section_id: "encounters",
    label: "Encounters",
    locked: true,
    date_filter_keys: ["recorded_date"],
    table: {
      columns: summaryColumns,
      rows: createRows("encounter", [
        {
          name: "Primary Care Follow-up",
          category: "Office Visit",
          status: "Signed",
          recorded_date: "2026-03-14",
        },
        {
          name: "Annual Wellness Visit",
          category: "Preventive",
          status: "Signed",
          recorded_date: "2026-01-22",
        },
        {
          name: "Urgent Care Visit",
          category: "Acute",
          status: "Signed",
          recorded_date: "2025-12-16",
        },
        {
          name: "Endocrinology Consult",
          category: "Specialty",
          status: "Signed",
          recorded_date: "2025-10-04",
        },
      ]),
    },
  },
  {
    section_id: "lab-results",
    label: "Lab Results",
    locked: true,
    date_filter_keys: ["recorded_date"],
    table: {
      columns: summaryColumns,
      rows: createRows("lab", [
        {
          name: "Hemoglobin A1c",
          category: "Chemistry",
          status: "Final",
          recorded_date: "2026-03-18",
        },
        {
          name: "Comprehensive Metabolic Panel",
          category: "Chemistry",
          status: "Final",
          recorded_date: "2026-03-18",
        },
        {
          name: "Lipid Panel",
          category: "Chemistry",
          status: "Final",
          recorded_date: "2026-02-07",
        },
        {
          name: "TSH",
          category: "Endocrine",
          status: "Final",
          recorded_date: "2025-12-28",
        },
        {
          name: "CBC",
          category: "Hematology",
          status: "Final",
          recorded_date: "2025-11-11",
        },
      ]),
    },
  },
  {
    section_id: "vitals",
    label: "Vitals",
    locked: true,
    date_filter_keys: ["recorded_date"],
    table: {
      columns: summaryColumns,
      rows: createRows("vital", [
        {
          name: "Blood Pressure",
          category: "Vital Sign",
          status: "Recorded",
          recorded_date: "2026-03-14",
        },
        {
          name: "Heart Rate",
          category: "Vital Sign",
          status: "Recorded",
          recorded_date: "2026-03-14",
        },
        {
          name: "Temperature",
          category: "Vital Sign",
          status: "Recorded",
          recorded_date: "2026-03-14",
        },
        {
          name: "Weight",
          category: "Anthropometric",
          status: "Recorded",
          recorded_date: "2026-01-22",
        },
      ]),
    },
  },
  {
    section_id: "notes",
    label: "Notes",
    locked: true,
    date_filter_keys: ["recorded_date"],
    table: {
      columns: summaryColumns,
      rows: createRows("note", [
        {
          name: "Provider Progress Note",
          category: "Clinical Note",
          status: "Signed",
          recorded_date: "2026-03-14",
        },
        {
          name: "Nursing Intake Note",
          category: "Nursing",
          status: "Signed",
          recorded_date: "2026-03-14",
        },
        {
          name: "Telephone Encounter",
          category: "Communication",
          status: "Signed",
          recorded_date: "2026-02-25",
        },
        {
          name: "Care Coordination Note",
          category: "Care Management",
          status: "Signed",
          recorded_date: "2025-12-01",
        },
      ]),
    },
  },
  {
    section_id: "family-history",
    label: "Family History",
    locked: true,
    table: {
      columns: summaryColumns,
      rows: createRows("family-history", [
        {
          name: "Father - Hypertension",
          category: "Cardiovascular",
          status: "Confirmed",
          recorded_date: "2025-10-21",
        },
        {
          name: "Mother - Type 2 Diabetes",
          category: "Endocrine",
          status: "Confirmed",
          recorded_date: "2025-10-21",
        },
        {
          name: "Brother - Asthma",
          category: "Respiratory",
          status: "Confirmed",
          recorded_date: "2025-08-30",
        },
        {
          name: "Maternal Grandmother - Stroke",
          category: "Neurologic",
          status: "Confirmed",
          recorded_date: "2025-08-30",
        },
      ]),
    },
  },
];
